import React, { useMemo, useState, useCallback, useRef, useImperativeHandle, forwardRef } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { buildCategoryPathParams } from '../../../../util/search';
import css from './TopbarMobileCategoriesMenu.module.css';
import usePreventScrollBounce from '../../../../hooks/usePreventScrollBounce';

const CategoryToggleIcon = ({ className, isOpen }) => (
  <svg
    className={className}
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M3 8h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path
      className={classNames(css.toggleVerticalLine, { [css.toggleVerticalLineOpen]: isOpen })}
      d="M3 8h10"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const ArrowRightIcon = ({ className }) => (
  <svg
    className={className}
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M3 8h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

const TopbarMobileCategoriesMenu = forwardRef((props, ref) => {
  const {
    categories = [],
    categoryLevelKeys = [],
    onCategorySelect,
    buildSearchUrl,
    onClose,
  } = props;

  const [expanded, setExpanded] = useState([]);
  const [closing, setClosing] = useState(false);
  const panelRef = useRef(null);
  usePreventScrollBounce(panelRef);

  const triggerClose = useCallback(() => {
    setClosing(true);
  }, []);

  useImperativeHandle(ref, () => ({ triggerClose }), [triggerClose]);

  const handleAnimationEnd = useCallback((e) => {
    if (closing && e.target === panelRef.current) {
      setClosing(false);
      onClose?.();
    }
  }, [closing, onClose]);

  const toggleCategory = useCallback(id => {
    setExpanded(prev => (prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]));
  }, []);

  // Modifier clicks fall through to the browser (new tab); plain clicks stay SPA.
  const handleSelect = useCallback(
    (event, path) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      onCategorySelect?.(buildCategoryPathParams(path, categoryLevelKeys));
    },
    [onCategorySelect, categoryLevelKeys]
  );

  const categoryUrl = useCallback(
    path => buildSearchUrl(buildCategoryPathParams(path, categoryLevelKeys)),
    [buildSearchUrl, categoryLevelKeys]
  );

  const renderCategoryNode = useCallback(
    (category, depth = 0, path = []) => {
      const hasChildren =
        Array.isArray(category?.subcategories) && category.subcategories.length > 0;
      const isExpanded = expanded.includes(category.id);
      const nextPath = [...path, category];
      const isTopLevel = depth === 0;
      return (
        <li key={category.id} className={classNames(css.categoryNode, { [css.categoryNodeTopLevel]: isTopLevel })}>
          <div
            className={classNames(css.categoryRow, {
              [css.categoryRowTopLevel]: isTopLevel,
            })}
            style={depth > 0 ? { paddingLeft: `${depth * 24}px` } : undefined}
          >
            <a
              href={categoryUrl(nextPath)}
              className={classNames(css.categorySelectButton, {
                [css.categorySelectButtonTopLevel]: isTopLevel,
                [css.categorySelectButtonChild]: !isTopLevel,
              })}
              onClick={event => handleSelect(event, nextPath)}
            >
              {category.name}
            </a>
            {hasChildren ? (
              <button
                type="button"
                className={css.categoryToggleButton}
                onClick={() => toggleCategory(category.id)}
                aria-expanded={isExpanded}
              >
                <CategoryToggleIcon className={css.toggleIcon} isOpen={isExpanded} />
              </button>
            ) : null}
          </div>
          {hasChildren ? (
            <div className={classNames(css.childListWrapper, { [css.childListWrapperOpen]: isExpanded })}>
              <ul className={css.childList}>
                {category.subcategories.map(sub => renderCategoryNode(sub, depth + 1, nextPath))}
              </ul>
            </div>
          ) : null}
        </li>
      );
    },
    [expanded, handleSelect, toggleCategory, categoryUrl]
  );

  const categoryList = useMemo(
    () => categories.map(category => renderCategoryNode(category)),
    [categories, renderCategoryNode]
  );

  return (
    <div
      className={classNames(css.panel, { [css.panelClosing]: closing })}
      ref={panelRef}
      onClick={e => e.stopPropagation()}
      onAnimationEnd={handleAnimationEnd}
    >
      <ul className={css.categoryList}>
        <li className={classNames(css.categoryNode, css.categoryNodeTopLevel)}>
          <div className={classNames(css.categoryRow, css.categoryRowTopLevel)}>
            <a
              href={categoryUrl([])}
              className={classNames(css.categorySelectButton, css.categorySelectButtonTopLevel)}
              onClick={event => handleSelect(event, [])}
            >
              <FormattedMessage id="TopbarDesktop.allListings" />
            </a>
            <ArrowRightIcon className={css.arrowIcon} />
          </div>
        </li>
        {categoryList}
      </ul>
    </div>
  );
});

export default TopbarMobileCategoriesMenu;
